/**
 * Shopping Cart Page
 * Display cart items with quantity controls, totals, and checkout button
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Main, Flex, Grid } from '../../../components/styled/Layout';
import { Card, CardContent } from '../../../components/styled/Card';
import { PrimaryButton, SecondaryButton, DangerButton } from '../../../components/styled/Button';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../../../contexts/AuthContext';
import apiService from '../../../services/apiService';
import useShopFeatures from '../hooks/useShopFeatures';
import { formatPrice } from '../utils/productHelpers';
import './Shop.css';

const Cart = () => {
  const { cart, cartCount, updateQuantity, removeItem, getTotal, clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const features = useShopFeatures();

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  if (cart.length === 0) {
    return (
      <Container>
        <Main>
          <div className="empty-state">
            <h2>🛒 Your cart is empty</h2>
            <p>Add some products to get started!</p>
            <PrimaryButton as={Link} to="/shop/catalog">
              Continue Shopping
            </PrimaryButton>
          </div>
        </Main>
      </Container>
    );
  }

  const subtotal = getTotal();
  const discount = appliedPromo ? (subtotal * appliedPromo.discountPercent / 100) : 0;
  const subtotalAfterDiscount = subtotal - discount;
  const shipping = subtotalAfterDiscount > 50 ? 0 : 9.99;
  const tax = subtotalAfterDiscount * 0.08; // 8% tax
  const total = subtotalAfterDiscount + shipping + tax;

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;

    setPromoLoading(true);
    setPromoError('');

    try {
      const response = await apiService.post('/shop/promo/validate', { code: promoCode });
      
      if (response.valid) {
        setAppliedPromo({
          code: promoCode,
          discountPercent: response.discountPercent || 10
        });
        setPromoCode('');
        setPromoError('');
      } else {
        setPromoError(response.message || 'Invalid promo code');
      }
    } catch (error) {
      console.error('Error applying promo:', error);
      setPromoError('Failed to apply promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoError('');
  };

  const handleCheckout = () => {
    if (isAuthenticated) {
      // Pass promo code to checkout if applied
      const checkoutUrl = appliedPromo 
        ? `/shop/checkout?promo=${encodeURIComponent(appliedPromo.code)}`
        : '/shop/checkout';
      navigate(checkoutUrl);
    } else {
      // Redirect to login with return URL
      navigate('/login?redirect=' + encodeURIComponent('/shop/checkout'));
    }
  };

  return (
    <Container>
      <Main>
        <div className="shop-page cart-page">
          <Flex justify="space-between" align="center" style={{ marginBottom: '24px' }}>
            <h1>Shopping Cart ({cartCount} items)</h1>
            <SecondaryButton onClick={clearCart}>
              Clear Cart
            </SecondaryButton>
          </Flex>

          <Grid columns={3} gap="var(--space-4)" style={{ gridTemplateColumns: '2fr 1fr', alignItems: 'start' }}>
            {/* Cart Items */}
            <div className="cart-items">
              {cart.map((item, index) => (
                <Card key={index} style={{ marginBottom: '16px' }}>
                  <CardContent>
                    <Flex gap="20px" align="start">
                      {/* Product Image */}
                      <div className="cart-item-image">
                        <img 
                          src={item.product.image} 
                          alt={item.product.name}
                          onClick={() => navigate(`/shop/product/${item.product.id}`)}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>

                      {/* Product Info */}
                      <div className="cart-item-info">
                        <h3 
                          className="cart-item-name"
                          onClick={() => navigate(`/shop/product/${item.product.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          {item.product.name}
                        </h3>
                        
                        {item.variation && Object.keys(item.variation).length > 0 && (
                          <div className="cart-item-variation">
                            {Object.entries(item.variation).map(([key, value]) => (
                              <span key={key}>{key}: {value}</span>
                            )).join(', ')}
                          </div>
                        )}

                        <p className="cart-item-price">
                          ${formatPrice(item.product.price)} each
                        </p>

                        {/* Quantity Controls */}
                        <Flex gap="12px" align="center" style={{ marginTop: '12px' }}>
                          <label>Quantity:</label>
                          <Flex gap="8px" align="center" className="quantity-controls">
                            <button 
                              onClick={() => updateQuantity(item.productId, Math.max(1, item.quantity - 1), item.variation)}
                              className="qty-btn"
                            >
                              -
                            </button>
                            <input 
                              type="number" 
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.productId, Math.max(1, parseInt(e.target.value) || 1), item.variation)}
                              min="1"
                              className="qty-input"
                            />
                            <button 
                              onClick={() => updateQuantity(item.productId, item.quantity + 1, item.variation)}
                              className="qty-btn"
                            >
                              +
                            </button>
                          </Flex>
                          
                          <DangerButton 
                            onClick={() => removeItem(item.productId, item.variation)}
                            size="small"
                            style={{ marginLeft: 'auto' }}
                          >
                            Remove
                          </DangerButton>
                        </Flex>

                        {/* Item Total */}
                        <p className="cart-item-total">
                          Subtotal: ${formatPrice(parseFloat(item.product.price) * item.quantity)}
                        </p>
                      </div>
                    </Flex>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Order Summary */}
            <Card className="cart-summary">
              <CardContent>
                <h2>Order Summary</h2>
                
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                
                <div className="summary-row">
                  <span>Shipping:</span>
                  <span>{shipping === 0 ? 'FREE' : `$${shipping.toFixed(2)}`}</span>
                </div>
                {shipping === 0 && (
                  <div className="free-shipping-message">
                    🎉 You qualify for free shipping!
                  </div>
                )}
                
                <div className="summary-row">
                  <span>Tax (8%):</span>
                  <span>${tax.toFixed(2)}</span>
                </div>

                {/* Promo Code Section */}
                {features.promoCodes && (
                  <div className="promo-section">
                    <div className="summary-divider" style={{ margin: '12px 0' }}></div>
                    
                    {appliedPromo ? (
                      <div className="promo-applied">
                        <Flex justify="space-between" align="center">
                          <div>
                            <span className="promo-badge">✓ {appliedPromo.code}</span>
                            <small style={{ display: 'block', color: '#28a745' }}>
                              {appliedPromo.discountPercent}% off applied
                            </small>
                          </div>
                          <button onClick={handleRemovePromo} className="promo-remove">
                            ✕
                          </button>
                        </Flex>
                        <div className="summary-row" style={{ color: '#28a745', marginTop: '8px' }}>
                          <span>Discount:</span>
                          <span>-${discount.toFixed(2)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="promo-input-group">
                        <Flex gap="8px">
                          <input
                            type="text"
                            placeholder="Enter promo code"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                            onKeyPress={(e) => e.key === 'Enter' && handleApplyPromo()}
                            className="promo-input"
                            disabled={promoLoading}
                          />
                          <SecondaryButton 
                            onClick={handleApplyPromo}
                            disabled={!promoCode.trim() || promoLoading}
                            size="small"
                          >
                            {promoLoading ? '...' : 'Apply'}
                          </SecondaryButton>
                        </Flex>
                        {promoError && (
                          <small style={{ color: '#dc3545', display: 'block', marginTop: '4px' }}>
                            {promoError}
                          </small>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="summary-divider"></div>
                
                <div className="summary-row summary-total">
                  <span>Total:</span>
                  <span>${total.toFixed(2)}</span>
                </div>

                <PrimaryButton 
                  onClick={handleCheckout}
                  style={{ width: '100%', marginTop: '20px', padding: '14px' }}
                >
                  {isAuthenticated ? 'Proceed to Checkout' : 'Login to Checkout'}
                </PrimaryButton>

                <SecondaryButton 
                  as={Link}
                  to="/shop/catalog"
                  style={{ width: '100%', marginTop: '12px' }}
                >
                  Continue Shopping
                </SecondaryButton>
              </CardContent>
            </Card>
          </Grid>
        </div>
      </Main>
    </Container>
  );
};

export default Cart;

